import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Chip, Grid, TableCell, TableRow } from "@mui/material";
import PropTypes from "prop-types";

import messages from "../../../../../assets/locale/messages";
import noVerificationPlaceholderImg from "../../../../../assets/images/placeholders/noVerificationPlaceholder.svg";
import noTransactionsPlaceholderImg from "../../../../../assets/images/placeholders/noTransactionsPlaceholder.svg";
import EmptyState from "../../../../../components/EmptyState";
import Table from "../../../../../components/Table";
import Pagination from "../../../../../components/Pagination";
import ViewUploadedFile from "../../../../../components/Upload/FileUpload/ViewUploadedFile";
import {
  getExporterTransactionsListRequest,
  getExporterVerificationInfoRequest
} from "../../../../../store/Users/actions";
import {
  CHIPS_CLASSES,
  EXPORTER_STATUS,
  OFF_PLATFORM_VERIFICATION_DOCUMENTS_TYPES,
  TRANSACTION_STATUS_IDS,
  VERIFICATION_METHODS
} from "../../../../../utils/Constants";
import {
  CURRENCIES_IDS,
  CURRENCIES_SYMBOLS
} from "../../../../../utils/Constants";
import { ShieldCheckSmallIcon } from "../../../../../utils/Icons/SheildCheckSmall";
import { formatDate } from "../../../../../utils/Helpers";
import {
  EmailSmallIcon,
  IdSmallIcon,
  PhoneSmallIcon,
  ProfileSmallIcon
} from "../../../../../utils/Icons";
import "./VerificationAndSubscription.scss";

const VerificationAndSubscription = ({ isCombinedUser }) => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const companyContactInfo = useSelector(
    (state) => state.users.exporterDetails.companyContactInfo
  );
  const verificationInfo = useSelector(
    (state) => state.users.exporterDetails.verificationInfo
  );
  const transactionsList = useSelector(
    (state) => state.users.exporterDetails.transactions.list
  );
  const transactionsMeta = useSelector(
    (state) => state.users.exporterDetails.transactions.meta
  );

  const lang = useSelector((state) => state.locale.lang);

  const { users } = messages[lang];

  const details = isCombinedUser
    ? users.combinedDetails
    : users.exporterDetails;

  const { subscription, verification, transactions, companyData } =
    details.subscriptionAndVerification;

  const [page, setPage] = useState(1);
  const [isUserVerified, setIsUserVerified] = useState(false);

  useEffect(() => {
    if (companyContactInfo?.UserStatusId) {
      setIsUserVerified(
        !![
          EXPORTER_STATUS.businessVerified,
          EXPORTER_STATUS.subscribed,
          EXPORTER_STATUS.expired
        ].includes(companyContactInfo?.UserStatusId)
      );
    }
  }, [companyContactInfo]);

  useEffect(() => {
    if (id && isUserVerified) {
      dispatch(
        getExporterVerificationInfoRequest({ params: { userId: id } })
      );
    }
  }, [isUserVerified]);

  useEffect(() => {
    if (id && isUserVerified) {
      dispatch(
        getExporterTransactionsListRequest({
          params: {
            page,
            userId: id
          }
        })
      );
    }
  }, [page, isUserVerified]);

  const renderLabelValueInfo = (icon, label, value) => (
    <div className="d-flex align-items-center">
      <div className="me-3">{icon}</div>
      <div>
        <p className="mb-1 text-light-grey fsize-13">{label}</p>
        <p className="mb-0 text-color fsize-15 fweight-600 word-break">
          {value || "__"}
        </p>
      </div>
    </div>
  );

  const renderTransactionsRows = (
    {
      PaymentReason,
      TransactionId,
      TransactionDate,
      Amount,
      CurrencyId,
      TransactionStatusId,
      TransactionStatusName
    },
    i
  ) => (
    <TableRow key={`transaction-table-${i}`}>
      <TableCell>{PaymentReason}</TableCell>
      <TableCell>
        <p className="mb-0 word-break w-100">{TransactionId}</p>
      </TableCell>
      <TableCell>
        {TransactionDate && formatDate(TransactionDate)}
      </TableCell>
      <TableCell>{`${CurrencyId === CURRENCIES_IDS.usd ? CURRENCIES_SYMBOLS.usd : CURRENCIES_SYMBOLS.euro} ${Amount}`}</TableCell>
      <TableCell>
        <Chip
          label={TransactionStatusName}
          className={
            TransactionStatusId === TRANSACTION_STATUS_IDS.success
              ? CHIPS_CLASSES.green
              : CHIPS_CLASSES.red
          }
        />
      </TableCell>
    </TableRow>
  );

  return (
    <div className="verification-subscription-container">
      {isUserVerified ? (
        <div>
          <Grid
            container
            columnSpacing={{ xs: 0, lg: 2 }}
            className="company-data mb-4">
            <Grid
              item
              xs={12}
              lg={3}
              sx={{
                marginBottom: {
                  xs: 4,
                  lg: 0
                }
              }}>
              <Grid
                container
                columnSpacing={{ xs: 0, sm: 2, lg: 0 }}
                sx={{ height: "100%" }}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  lg={12}
                  sx={{
                    height: {
                      lg: "62%"
                    },
                    marginBottom: {
                      xs: 3,
                      sm: 0,
                      lg: "8%"
                    }
                  }}>
                  <div className="plan-section white-bg border-radius-10 p-4 h-100 d-flex align-items-center">
                    {companyContactInfo?.UserStatusId ===
                      EXPORTER_STATUS.subscribed &&
                    verificationInfo?.SubscriptionType ? (
                      <div>
                        <h6 className="fsize-17 fweight-600 title-color mb-3">
                          {subscription.pricingPlan}
                        </h6>
                        <img
                          src={`data:image/svg+xml;utf8,${encodeURIComponent(
                            verificationInfo?.SubscriptionType?.Icon
                          )}`}
                          className="mb-2"
                          style={{ width: "4rem" }}
                        />
                        <p className="fsize-22 fweight-600 title-color mb-1">{`${verificationInfo?.SubscriptionType?.Name} ${subscription.plan}`}</p>
                        <p className="mb-2 d-flex align-items-center">
                          <span className="dark-blue-text fsize-24 fweight-700 me-1">
                            <span>
                              {verificationInfo?.SubscriptionType
                                ?.CurrencyId === CURRENCIES_IDS.usd
                                ? CURRENCIES_SYMBOLS.usd
                                : CURRENCIES_SYMBOLS.euro}
                            </span>
                            {
                              verificationInfo?.SubscriptionType
                                ?.Price
                            }
                          </span>
                          <span className="fsize-16 title-color">{`/ ${subscription.year}`}</span>
                        </p>
                        <p className="light-text-color fsize-13 mb-0">
                          {subscription.renewalData}
                        </p>
                        <p className="fize-15 fweight-600 text-color mb-0">
                          {!!verificationInfo?.SubscriptionType
                            ?.RenewalDate &&
                            formatDate(
                              verificationInfo?.SubscriptionType
                                ?.RenewalDate
                            )}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <EmptyState
                          subTitle={
                            companyContactInfo?.UserStatusId ===
                            EXPORTER_STATUS.expired
                              ? subscription.subscriptionExpired
                              : subscription.notSubscribed
                          }
                          imgSrc={noVerificationPlaceholderImg}
                          className={"empty-plan-placeholder"}
                        />
                      </div>
                    )}
                  </div>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  lg={12}
                  sx={{
                    height: {
                      lg: "33%"
                    }
                  }}>
                  <div className="verification-section white-bg border-radius-10 p-4 h-100 d-flex align-items-xl-center align-items-start">
                    <div>
                      <h6 className="fsize-17 fweight-600 title-color mb-3">
                        {verification.verificationMethod}
                      </h6>
                      {renderLabelValueInfo(
                        <ShieldCheckSmallIcon />,
                        verification.verifiedBy,
                        `${verificationInfo?.BusinessVerificationTypeName} ${verification.method}`
                      )}
                    </div>
                  </div>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} lg={9}>
              <div className="white-bg border-radius-10 p-4 pb-3 h-100 d-flex flex-column justify-content-between">
                <div>
                  <h6 className="fsize-17 fweight-600 title-color mb-3">
                    {transactions.transactions}
                  </h6>
                  {!!transactionsList?.length ? (
                    <Table
                      className={"transactions-table"}
                      headlines={[
                        transactions.paymentReason,
                        transactions.transactionId,
                        transactions.transactionDate,
                        transactions.amount,
                        transactions.status
                      ]}
                      rows={
                        !isLoading &&
                        transactionsList?.map((transaction, i) =>
                          renderTransactionsRows(transaction, i)
                        )
                      }
                    />
                  ) : (
                    !isLoading && (
                      <EmptyState
                        subTitle={transactions.noTransactions}
                        imgSrc={noTransactionsPlaceholderImg}
                      />
                    )
                  )}
                </div>
                {!!transactionsList?.length && (
                  <div className="d-flex justify-content-center pt-2">
                    <Pagination
                      count={transactionsMeta?.pages}
                      page={transactionsMeta?.currentPage || page}
                      handleChange={(page) => {
                        setPage(page);
                      }}
                      defaultPage={1}
                    />
                  </div>
                )}
              </div>
            </Grid>
          </Grid>
          <div className="company-documents">
            {verificationInfo?.BusinessVerificationTypeId !==
              VERIFICATION_METHODS.onlinePayment && (
              <div className="white-bg border-radius-10 p-4 mb-4">
                <h6 className="fsize-17 fweight-600 title-color mb-3">
                  {companyData.companyDocuments}
                </h6>
                <p className="fsize-13 title-color">
                  {companyData.uploadedDocuments}
                </p>
                <Grid container>
                  {verificationInfo?.BusinessVerificationTypeId ===
                    VERIFICATION_METHODS.bankTransfer && (
                    <Grid item xs={12} lg={6}>
                      <ViewUploadedFile
                        file={verificationInfo?.DefaultDocument}
                        type={
                          verificationInfo?.OriginlDefaultDocumentName?.includes(
                            "pdf"
                          )
                            ? "pdf"
                            : "image"
                        }
                        name={
                          verificationInfo?.OriginlDefaultDocumentName
                        }
                        viewMode
                        isFileLink
                      />
                    </Grid>
                  )}
                  {verificationInfo?.BusinessVerificationTypeId ===
                    VERIFICATION_METHODS.offPlatform &&
                    verificationInfo?.CompanyData?.CompanyDocuments?.map(
                      ({
                        Id,
                        DocumentName,
                        OriginalDocumentName,
                        DocumentTypeId
                      }) => (
                        <Grid
                          item
                          xs={12}
                          lg={6}
                          className="mb-3"
                          key={`company-documents-${Id}`}>
                          <ViewUploadedFile
                            file={DocumentName}
                            name={OriginalDocumentName}
                            type={
                              DocumentTypeId ===
                              OFF_PLATFORM_VERIFICATION_DOCUMENTS_TYPES.pdf
                                ? "pdf"
                                : "image"
                            }
                            viewMode
                            isFileLink
                          />
                        </Grid>
                      )
                    )}
                </Grid>
              </div>
            )}
            {verificationInfo?.BusinessVerificationTypeId ===
              VERIFICATION_METHODS.offPlatform && (
              <div className="white-bg border-radius-10 p-4">
                <h6 className="fsize-17 fweight-600 title-color mb-3">
                  {companyData.companyRepresentativeInfo}
                </h6>
                <Grid container>
                  <Grid item xs={12} lg={6} className="mb-4">
                    {renderLabelValueInfo(
                      <ProfileSmallIcon />,
                      companyData.representativeName,
                      verificationInfo?.CompanyData
                        ?.RepresentativeName
                    )}
                  </Grid>
                  <Grid item xs={12} lg={6} className="mb-4">
                    {renderLabelValueInfo(
                      <EmailSmallIcon />,
                      companyData.emailAdress,
                      verificationInfo?.CompanyData?.Email
                    )}
                  </Grid>
                  <Grid item xs={12} lg={6} className="mb-4">
                    {renderLabelValueInfo(
                      <PhoneSmallIcon />,
                      companyData.mobileNumber,
                      verificationInfo?.CompanyData?.MobileNumber
                    )}
                  </Grid>
                  <Grid item xs={12} lg={6} className="mb-4">
                    {renderLabelValueInfo(
                      <IdSmallIcon />,
                      companyData.jobTitle,
                      verificationInfo?.CompanyData?.JobTitle
                    )}
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <p className="fsize-13 title-color">
                      {companyData.representativeId}
                    </p>
                    <ViewUploadedFile
                      file={
                        verificationInfo?.CompanyData
                          ?.RepresentativeIDDoc
                      }
                      type={
                        verificationInfo?.CompanyData?.OriginalRepresentativeIDDoc?.includes(
                          "pdf"
                        )
                          ? "pdf"
                          : "image"
                      }
                      name={
                        verificationInfo?.CompanyData
                          ?.OriginalRepresentativeIDDoc
                      }
                      viewMode
                      isFileLink
                    />
                  </Grid>
                </Grid>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="white-bg border-radius-10 d-flex justify-content-center align-items-center">
          {!isLoading && (
            <EmptyState
              subTitle={verification.notVerified}
              imgSrc={noVerificationPlaceholderImg}
            />
          )}
        </div>
      )}
    </div>
  );
};

VerificationAndSubscription.propTypes = {
  isCombinedUser: PropTypes.bool
};

VerificationAndSubscription.defaultProps = {
  isCombinedUser: false
};

export default VerificationAndSubscription;
